import React, { useMemo } from "react"
import MyTooltip from "@common-utils/MyTooltip"
import { Form, Formik } from "formik"
import MyInputField from "@common-utils/fields/MyInputField"
import MySimpleSpinner from "@common-utils/MySimpleSpinner"
import { useAnnoncesListingContext } from "./AnnoncesListing"
import { CONST_ANNONCE_ETAT_ACTIVE } from "@common-constants/*"
import { useAuth } from "../../../../AppContext"
import { isEqual, sortBy } from "lodash"

const AnnoncesListingSearchBar = () => {
     const globalContext = useAnnoncesListingContext()
     const { currentUser } = useAuth()

     // 1. Define Defaults
     const defaultValues = useMemo(() => {
          return {
               etat: CONST_ANNONCE_ETAT_ACTIVE,
               responsables: currentUser?.teamRecrutement?.map(item => item.id as number) || [],
          }
     }, [currentUser])

     // 2. Calculate Active Filter Count
     const activeFilterCount = useMemo(() => {
          let count = 0
          const current = globalContext.filters

          if (current.keyword && current.keyword.trim() !== "") count++
          if (current.etat !== defaultValues.etat) count++

          const currentResp = current.responsables || []
          const defaultResp = defaultValues.responsables
          if (!isEqual(sortBy(currentResp), sortBy(defaultResp))) {
               count++
          }

          if (current.entites && current.entites.length > 0) count++
          if (current.categoriesMetier && current.categoriesMetier.length > 0) count++
          if (current.villes && current.villes.length > 0) count++

          return count
     }, [globalContext.filters, defaultValues])

     function handleSubmit(values: { keyword?: string }) {
          globalContext.setFilters(prev => ({
               ...prev,
               keyword: values.keyword,
               page: 1,
          }))
          globalContext.setItemInPreview(null)
     }

     function handleReset() {
          const defaultFilters = {
               page: 1,
               pageLength: 20,
               etat: defaultValues.etat,
               responsables: defaultValues.responsables,
               keyword: "",
               entites: [],
               categoriesMetier: [],
               villes: [],
          }

          globalContext.setFilters(defaultFilters)

          if (globalContext.simpleSearchBarFormikRef.current) {
               globalContext.simpleSearchBarFormikRef.current.resetForm({ values: { keyword: "" } })
               globalContext.simpleSearchBarFormikRef.current.setSubmitting(true)
          }

          if (globalContext.filtersFormikRef.current) {
               globalContext.filtersFormikRef.current.resetForm({ values: defaultFilters })
          }

          globalContext.setItemInPreview(null)
     }

     function handleClickShowFilters() {
          globalContext.setAreFiltersVisible(prev => !prev)
          globalContext.setItemInPreview(prev => {
               if (prev) {
                    return { ...prev, isPreviewVisible: false }
               }
               return null
          })
     }

     return (
          <Formik<{ keyword?: string }>
               initialValues={{ keyword: globalContext.filters.keyword || "" }}
               onSubmit={handleSubmit}
               innerRef={globalContext.simpleSearchBarFormikRef}
               enableReinitialize
          >
               {helpers => {
                    return (
                         <Form noValidate autoComplete={"off"}>
                              <div className="input-group">
                                   <MyTooltip title={"Voir les filtres"}>
                                        <button type={"button"} className={"btn btn-sm btn-outline btn-outline-secondary position-relative"} onClick={handleClickShowFilters}>
                                             <i className={"fas fa-filter fs-4 text-dark"} />

                                             {/* 🔹 Badge moved to Left (start-0) with Light Danger style */}
                                             {activeFilterCount > 0 && (
                                                  <span
                                                       className="position-absolute top-0 start-0 translate-middle badge rounded-pill badge-light-warning border border-warning d-flex align-items-center justify-content-center p-0"
                                                       style={{
                                                            width: "15px",
                                                            height: "15px",
                                                            fontSize: "9px",
                                                       }}
                                                  >
                                                       {activeFilterCount}
                                                  </span>
                                             )}
                                        </button>
                                   </MyTooltip>

                                   {/* Bulk selection button */}
                                   {globalContext.listingQuery.data && globalContext.listingQuery.data.totalElementsInAllPages > 1 && (
                                        <MyTooltip
                                             title={globalContext.isBulkSelectionEnabled.enabled ? "Quitter le mode sélection multiple" : "Activer le mode sélection multiple"}
                                        >
                                             <button
                                                  type={"button"}
                                                  className={`btn btn-sm ${globalContext.isBulkSelectionEnabled.enabled ? "btn-secondary" : "btn-outline btn-outline-secondary"}`}
                                                  onClick={() =>
                                                       globalContext.setIsBulkSelectionEnabled(prev =>
                                                            prev.enabled
                                                                 ? {
                                                                        enabled: false,
                                                                        checkedElementsInPage: [],
                                                                        areAllElementsInAllPagesChecked: false,
                                                                   }
                                                                 : {
                                                                        ...prev,
                                                                        enabled: true,
                                                                   }
                                                       )
                                                  }
                                             >
                                                  <i
                                                       className={`fas ${
                                                            globalContext.isBulkSelectionEnabled.enabled ? "fa-times text-danger fs-2" : "fa-list-check text-dark fs-4"
                                                       }`}
                                                  />
                                             </button>
                                        </MyTooltip>
                                   )}

                                   <MyInputField
                                        name={"keyword"}
                                        value={helpers.values.keyword}
                                        isInvalid={false}
                                        placeholder={"Rechercher ..."}
                                        onChange={val => {
                                             helpers.setFieldValue("keyword", val).then()
                                             if (globalContext.filtersFormikRef.current) {
                                                  globalContext.filtersFormikRef.current.setFieldValue("keyword", val).then()
                                             }
                                        }}
                                        classNames={"border"}
                                   />
                                   <button type={"submit"} className={"btn btn-sm btn-outline btn-outline-secondary"} disabled={helpers.isSubmitting}>
                                        {helpers.isSubmitting ? <MySimpleSpinner size={"sm"} classNames={"text-dark"} /> : <i className={"fas fa-search text-dark"} />}
                                   </button>

                                   <MyTooltip title={"Réinitialiser les filtres"}>
                                        <button
                                             type={"button"}
                                             className={`btn btn-sm btn-outline btn-outline-secondary ${activeFilterCount > 0 ? "text-primary" : ""}`}
                                             onClick={() => handleReset()}
                                             disabled={helpers.isSubmitting}
                                        >
                                             <i className={"fas fa-sync text-dark"} />
                                        </button>
                                   </MyTooltip>
                              </div>
                         </Form>
                    )
               }}
          </Formik>
     )
}

export default AnnoncesListingSearchBar
